<template>
    <section class="w-full h-full">
        <div class="h-full px-6 py-10 lg:py-20 xl:py-24 xl:px-48 3xl:px-96 3xl:py-32">
            <div class="h-full bg-white px-4 py-4">
                <div class="md:grid md:gap-10 md:grid-cols-10 mt-10">
                    <aside class="md:col-span-4">
                        <h2 class="font-semibold text-base xl:text-xl">
                            Select a Campaign or Fund
                        </h2>
                        <div class="pt-6 pb-4">
                            <p class="font-semibold text-gray-500 uppercase text-xs">Funds</p>
                            <div class="py-1">
                                <button
                                    @click="setSelectedCampaign(fund)" 
                                    v-for="fund in funds" :key="fund.id"
                                    class="cursor-pointer bg-white border-1 border-gray-300 hover:bg-gray-50 rounded-lg shadow-lg w-full"
                                    :class="{
                                        'border-green-300 border-2 text-green-500' : selectedCampaign && selectedCampaign.id === fund.id
                                    }">
                                    <div class="px-2 py-3 text-base xl:text-lg flex justify-center space-x-2">
                                        <span class="my-auto">
                                            {{ fund.name }}
                                        </span>
                                        <span
                                            v-if="selectedCampaign && selectedCampaign.id === fund.id"
                                            class="my-auto text-green-500">
                                            <svg 
                                                xmlns="http://www.w3.org/2000/svg" 
                                                fill="none" 
                                                viewBox="0 0 24 24" 
                                                stroke-width="1.5" 
                                                stroke="currentColor" class="w-5 h-5 xl:w-6 xl:h-6">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                            </svg>
                                        </span>
                                    </div>
                                </button>
                            </div>
                        </div>
                        <div class="py-2">
                            <p class="font-semibold text-gray-500 uppercase text-xs pb-2">Client Campaigns</p>
                            <div v-if="clientCampaigns.length" class="py-1">
                                <button
                                    @click="setSelectedCampaign(campaign)" 
                                    v-for="campaign in clientCampaigns" :key="campaign.id"
                                    class="cursor-pointer bg-white border-1 border-gray-300 hover:bg-gray-50 rounded-lg shadow-lg w-full"
                                    :class="{
                                        'border-green-300 border-2 text-green-500' : selectedCampaign && selectedCampaign.id === campaign.id
                                    }">
                                    <div class="px-2 py-3 text-base xl:text-lg flex justify-center space-x-2">
                                        <span class="my-auto">
                                            {{ campaign.name }}
                                        </span>
                                        <span
                                            v-if="selectedCampaign && selectedCampaign.id === campaign.id"
                                            class="my-auto text-green-500">
                                            <svg 
                                                xmlns="http://www.w3.org/2000/svg" 
                                                fill="none" 
                                                viewBox="0 0 24 24" 
                                                stroke-width="1.5" 
                                                stroke="currentColor" class="w-5 h-5 xl:w-6 xl:h-6">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                            </svg>
                                        </span>
                                    </div>
                                </button>
                            </div>
                            <div v-else class="text-sm px-2 py-2 bg-green-50 rounded-lg border-1 border-green-200">
                                There are no Client Campaigns at this time.
                            </div>
                        </div>
                    </aside>
                    <aside class="py-4 md:py-0 md:col-span-6">
                        <h2 class="font-semibold text-base xl:text-xl md:text-center">
                            Choose an Amount
                        </h2>
                        <div class="py-6 xl:px-10 space-y-10">
                            <div class="w-full space-y-6">
                                <button
                                    @click="setDonationAmount(50)"
                                    class="px-4 py-3 rounded-xl block bg-gray-50 border w-full hover:shadow-md"
                                    :class="{
                                        'shadow-lg bg-dark-green border-dark-green text-white' : donationAmount === 50
                                    }">
                                    <span class="text-xl xl:text-2xl">$</span>
                                    <span class="text-2xl xl:text-3xl">50</span>
                                </button>
                                <button
                                    @click="setDonationAmount(100)"
                                    class="px-4 py-2 rounded-xl block bg-gray-50 border w-full hover:shadow-md"
                                    :class="{
                                        'shadow-xl bg-dark-green border-dark-green' : donationAmount === 100
                                    }">
                                    <span class="text-xl xl:text-2xl">$</span>
                                    <span class="text-2xl xl:text-3xl">100</span>
                                </button>
                                <button
                                    @click="setDonationAmount(200)"
                                    class="px-4 py-3 rounded-xl block bg-gray-50 border w-full hover:shadow-md"
                                    :class="{
                                        'shadow-xl bg-dark-green border-dark-green' : donationAmount === 200
                                    }">
                                    <span class="text-xl xl:text-2xl">$</span>
                                    <span class="text-2xl xl:text-3xl">200</span>
                                </button>
                                <div>
                                    <p class="font-bold text-sm pb-2">Other Amount</p>
                                    <input
                                        v-model="customAmount"
                                        id="otherAmount"
                                        type="number"
                                        placeholder="250"
                                        class="px-4 py-3 w-full text-center rounded-xl border-gray-200 shadow-inner focus:outline-none focus:ring-2 focus:border-transparent  focus:ring-dark-green text-2xl xl:text-3xl"
                                    >
                                </div>
                            </div>
                            <div>
                                <p v-if="selectedCampaign" class="pb-4 text-xs">
                                    Fund/Campaign: 
                                    <span class="text-green-600">{{ selectedCampaign.name }}</span>
                                </p>
                                <p v-else class="pb-4 text-xs text-red-500">Please select a fund or campaign to continue.</p>
                                <button
                                    @click="initStripe"
                                    :disabled="!enableSubmit" 
                                    class="px-4 py-3 rounded-xl flex justify-center bg-black text-white shadow-md w-full space-x-4 hover:bg-gray-900"
                                    :class="{
                                        'cursor-not-allowed' : !enableSubmit
                                    }">
                                    <span class="my-auto" v-if="submitting">
                                        <SpinnerIcon />
                                    </span>
                                    <span class="text-xl">Donate</span>
                                    <span v-if="donationAmount || (customAmount && customAmount !== '')" 
                                        class="text-2xl">
                                        ${{ donationAmount ? donationAmount : customAmount }}
                                    </span>
                                </button>
                                <p class="text-sm mt-4 text-center">We are a 501c3 organization and all donations are tax deductable.</p>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import { randomString } from '@/helpers/utils'
import axios from 'axios'
import SpinnerIcon from '@/components/SpinnerIcon'
export default {
    name: 'DonationPage',
    components: {
        SpinnerIcon
    },
    data() {
        return {
            funds: [
                {
                    id: 'al8x25ajyhb',
                    name: 'Therapy Fund',
                    type: 'fund'
                }
            ],

            clientCampaigns: [
                // {
                //     id: '10wiqkxgy5on',
                //     name: "Jenna's Campaign",
                //     type: 'campaign'
                // }
            ],

            selectedCampaign: null,
            customAmount: null,
            donationAmount: 50,
            submitting: false,
            donationType: 'therapy-fund'
        }
    },

    computed: {
        enableSubmit(){
            if (!this.selectedCampaign || this.selectedCampaign === '') return false
            if (!this.donationAmount && (!this.customAmount || this.customAmount === '' || this.customAmount == '0')) return false
            return true
        }
    },

    mounted(){
        this.setSelectedCampaign(this.funds[0])
        this.checkStripeStatus()
    },

    watch: {
        customAmount(){
            if (this.customAmount && this.customAmount !== ''){
                this.donationAmount = null
            }
        }
    },

    methods: {
        checkStripeStatus(){
            const status = window.localStorage.getItem('stripe_status')
            if (status && status === 'success'){
                this.$swal({
                    icon: 'success',
                    title: 'Success',
                    text: 'Your donation was processed successfully!'
                }).then((e) => {
                    window.localStorage.removeItem('stripe_status')
                })
            }

            if (status && status === 'cancelled'){
                this.$swal({
                    icon: 'error',
                    title: 'Cancelled',
                    text: 'Donation cancelled'
                }).then((e) => {
                    window.localStorage.removeItem('stripe_status')
                })
            }
        },
        setSelectedCampaign(campaign){
            if (campaign){
                if (this.selectedCampaign && this.selectedCampaign.id === campaign.id) return
                // const query = {}
                // query[campaign.type] = campaign.id
                // this.$router.push({path: 'donate', query})
                return this.selectedCampaign = campaign
            }

            const fundQuery = this.$route.query.fund
            const campaignQuery = this.$route.query.campaign

            if (fundQuery) {
                const fund = this.funds.find((item) => item.id === fundQuery)
                if (fund) return this.selectedCampaign = fund
            }

            if (campaignQuery){
                const campaign = this.clientCampaigns.find((item) => item.id === campaignQuery)
                if (campaign) return this.selectedCampaign = campaign
            }
        },

        setDonationAmount(amount){
            if(!amount) return
            this.customAmount = null
            this.donationAmount = amount
        },

        async initStripe(){
            const itemName = this.selectedCampaign.name
            const amount = parseInt(this.donationAmount ? this.donationAmount : (this.customAmount && this.customAmount !== '' ? this.customAmount : 0))

            if (!amount) return this.$swal({
                icon: 'warning',
                text: 'A minimum of $1 is required.'
            })
            this.submitting = true

            try {
                const res = await axios.post('/api/stripe-handle', {
                line_items: [
                    {
                        price_data: {
                            currency: 'usd',
                            product_data: {
                                name: `${itemName} - Donation`
                            },
                            unit_amount: parseInt(`${amount}00`)
                        },
                        quantity: 1,
                    }
                ],
                donation_type: this.donationType
            })

            this.submitting = false

            if (res.data.success){
                window.location.href = res.data.stripe_session_url
                
            } else {
                this.$swal({
                    icon: 'error',
                    title: 'Error',
                    text: 'An error occured while trying to process your donation.'
                })
            }
            } catch (error) {
                console.error(error)
                this.submitting = false
            }

            
        }
    }
    
}
</script>

<style scoped>

</style>